import { sendMessageToSentry } from '@/utilities/sentry.js';

// reCAPTCHA Enterprise configuration
const RECAPTCHA_CONFIG = {
  scoreBasedSiteKey: process.env.RECAPTCHA_KEY || '6LeLULsaAAAAAHfVcBR9uFyd3NkCzXHdK08TPfof',
  checkboxSiteKey: process.env.RECAPTCHA_CHECKBOX_KEY || '6LeLULsaAAAAAHfVcBR9uFyd3NkCzXHdK08TPfof', // Replace with actual checkbox key
  get scriptUrl() {
    return `https://www.google.com/recaptcha/enterprise.js?render=${this.scoreBasedSiteKey}`;
  },
};

// State management for reCAPTCHA instances
const recaptchaState = {
  isLoaded: false,
  scoreBasedInstance: null,
  checkboxInstance: null,
  checkboxWidgetId: null,
  currentMode: 'score', // 'score' or 'checkbox'
  checkboxAction: '', // Store the action for checkbox mode
};

/**
 * Load reCAPTCHA Enterprise script with explicit render mode
 */
export async function loadRecaptchaScript() {
  if (recaptchaState.isLoaded) {
    return Promise.resolve();
  }

  // If reCAPTCHA was already loaded by index.html (defer), reuse it directly
  if (window.grecaptcha?.enterprise) {
    recaptchaState.isLoaded = true;
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    // Remove existing reCAPTCHA script if any
    const existingScript = document.querySelector('script[src*="recaptcha"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.src = RECAPTCHA_CONFIG.scriptUrl;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      recaptchaState.isLoaded = true;
      resolve();
    };

    script.onerror = () => {
      reject(new Error('Failed to load reCAPTCHA Enterprise script'));
    };

    document.head.appendChild(script);
  });
}

/**
 * Initialize score-based (invisible) reCAPTCHA
 */
export function initializeScoreBasedRecaptcha() {
  if (!window.grecaptcha || !window.grecaptcha.enterprise) {
    throw new Error('reCAPTCHA Enterprise not loaded');
  }

  const execute = (action) => {
    return new Promise((resolve, reject) => {
      window.grecaptcha.enterprise.ready(() => {
        try {
          window.grecaptcha.enterprise
            .execute(RECAPTCHA_CONFIG.scoreBasedSiteKey, { action })
            .then((token) => {
              resolve({
                token,
                siteKey: RECAPTCHA_CONFIG.scoreBasedSiteKey,
                mode: 'score',
              });
            })
            .catch((err) => {
              sendMessageToSentry('RecaptchaScoreExecuteFail', {
                level: 'error',
                extra: [
                  { name: 'action', value: action },
                  { name: 'error', value: err.message },
                ],
              });
              reject(err);
            });
        } catch (err) {
          reject(err);
        }
      });
    });
  };

  recaptchaState.scoreBasedInstance = { execute };
  recaptchaState.currentMode = 'score';
  return recaptchaState.scoreBasedInstance;
}

/**
 * Initialize checkbox reCAPTCHA with action
 */
export function initializeCheckboxRecaptcha(containerId = 'recaptcha-checkbox-container', action = '') {
  if (!window.grecaptcha || !window.grecaptcha.enterprise) {
    throw new Error('reCAPTCHA Enterprise not loaded');
  }

  return new Promise((resolve, reject) => {
    try {
      // Create container if it doesn't exist
      let container = document.getElementById(containerId);
      if (!container) {
        container = document.createElement('div');
        container.id = containerId;
        document.body.appendChild(container);
      } else {
        // Container exists - clear its content to prevent "already rendered" error
        container.innerHTML = '';
      }

      // Clear any existing widget
      if (recaptchaState.checkboxWidgetId !== null) {
        window.grecaptcha.enterprise.reset(recaptchaState.checkboxWidgetId);
      }

      // Store the action for later use
      recaptchaState.checkboxAction = action;

      // Render checkbox widget with action parameter
      const renderOptions = {
        sitekey: RECAPTCHA_CONFIG.checkboxSiteKey,
        callback: (token) => {
          // console.log('Checkbox reCAPTCHA completed with token:', token);
          // Return the token with metadata
          resolve({
            token,
            siteKey: RECAPTCHA_CONFIG.checkboxSiteKey,
            mode: 'checkbox',
            action: action || '',
          });
        },
        'error-callback': (error) => {
          console.error('reCAPTCHA checkbox error:', error);
          reject(new Error('reCAPTCHA checkbox verification failed'));
        },
      };
      
      // Add action to render options if provided
      if (action) {
        renderOptions.action = action;
      }
      
      recaptchaState.checkboxWidgetId = window.grecaptcha.enterprise.render(containerId, renderOptions);

      recaptchaState.currentMode = 'checkbox';
    } catch (error) {
      sendMessageToSentry('RecaptchaCheckboxInitFail', {
        level: 'error',
        extra: [
          {
            name: 'error',
            value: error.message,
          },
        ],
      });
      reject(error);
    }
  });
}

/**
 * Execute reCAPTCHA based on current mode
 */
export async function executeRecaptcha(action) {
  if (recaptchaState.currentMode === 'score' && recaptchaState.scoreBasedInstance) {
    return recaptchaState.scoreBasedInstance.execute(action);
  } else if (recaptchaState.currentMode === 'checkbox') {
    // For checkbox mode, the token is returned via callback
    throw new Error('Use initializeCheckboxRecaptcha for checkbox mode');
  } else {
    throw new Error('reCAPTCHA not initialized');
  }
}

/**
 * Reset reCAPTCHA to score-based mode
 */
export function resetToScoreMode() {
  if (recaptchaState.checkboxWidgetId !== null && window.grecaptcha?.enterprise) {
    window.grecaptcha.enterprise.reset(recaptchaState.checkboxWidgetId);
    recaptchaState.checkboxWidgetId = null;
  }
  recaptchaState.currentMode = 'score';
  recaptchaState.checkboxAction = '';
  
  // Remove all possible checkbox containers
  const containers = ['recaptcha-checkbox-container', 'recaptcha-checkbox-modal'];
  containers.forEach(id => {
    const container = document.getElementById(id);
    if (container) {
      container.remove();
    }
  });
}

/**
 * Get current reCAPTCHA mode
 */
export function getCurrentMode() {
  return recaptchaState.currentMode;
}

/**
 * Hash site key using SHA-256
 */
export async function hashSiteKey(siteKey) {
  const encoder = new TextEncoder();
  const data = encoder.encode(siteKey);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

export default {
  loadRecaptchaScript,
  initializeScoreBasedRecaptcha,
  initializeCheckboxRecaptcha,
  executeRecaptcha,
  resetToScoreMode,
  getCurrentMode,
  hashSiteKey,
  RECAPTCHA_CONFIG,
};
